import { useEffect, useState } from "react";
import { api, ApiError, debugMode, lastTrace,
         type MetricMeta, type MetricResult, type Property,
         type EntitySeries } from "../lib/api";
import { Line, Stacked, Table } from "../components/Viz";
import { Explainer } from "../components/Explainer";
import { DateRangeSlider } from "../components/DateRangeSlider";
import { EntityDetail } from "../components/EntityDetail";
import { DebugPanel } from "../components/DebugPanel";
import { useDemo } from "../lib/demo";

export function Dashboard({ property, connectionId, onChangeSources }: {
  property: Property;
  connectionId: string | null;
  onChangeSources: () => void;
}) {
  // The Search Console export is what every current analysis reads from.
  const gsc = property.sources.find((s) => s.kind === "gsc_bq");
  const source = gsc?.bq_project && gsc?.bq_dataset
    ? { project: gsc.bq_project, dataset: gsc.bq_dataset, location: gsc.bq_location }
    : null;
  const { demo } = useDemo();
  const [metrics, setMetrics] = useState<MetricMeta[]>([]);
  const [active, setActive] = useState<string>("coverage");
  const earliest = gsc?.earliest_date ?? isoOf(Date.now() - 486 * 86400000);
  const latest = isoOf(Date.now() - 3 * 86400000);
  const [range, setRange] = useState(() => ({
    from: isoOf(Math.max(Date.parse(earliest), Date.now() - 90 * 86400000)),
    to: latest,
  }));
  const [result, setResult] = useState<MetricResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [desc, setDesc] = useState(true);

  // One row open at a time, cached so reopening costs nothing.
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, EntitySeries>>({});
  const [detailBusy, setDetailBusy] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => { api.metrics().then((r) => setMetrics(r.metrics)).catch(() => {}); }, []);

  // The export's own latest date is the ceiling. Asking for today would
  // silently return a short window.
  const { from, to } = range;

  useEffect(() => {
    if (!metrics.length || !source || !connectionId) return;
    let cancelled = false;
    setBusy(true); setError(null); setResult(null); setSortKey(null);
    setOpenRow(null); setDetails({}); setDetailError(null);
    api.metric({
      connection_id: connectionId,
      metric: active,
      project: source.project,
      dataset: source.dataset,
      location: source.location,
      from, to,
    })
      .then((r) => { if (!cancelled) setResult(r); })
      .catch((e) => {
        if (cancelled) return;
        const err = e as ApiError;
        setFailure(err.body);
        setError(err.hint ?? `Query failed (${err.status}${err.code ? ` ${err.code}` : ""}).`);
      })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [active, from, to, connectionId, metrics.length, property.id]);

  const def = metrics.find((m) => m.id === active);
  const rows = sortRows(result?.rows ?? [], sortKey, desc);

  // The expandable column is whichever one carries the entity value.
  const entityKey = def?.entity
    ? def.columns.find((c) => c.key === def.entity)?.key ?? null
    : null;

  async function toggleRow(value: string) {
    if (openRow === value) { setOpenRow(null); return; }
    setOpenRow(value);
    setDetailError(null);
    if (details[value] || !def?.entity || !source || !connectionId) return;

    setDetailBusy(value);
    try {
      const s = await api.entity({
        connection_id: connectionId,
        project: source.project,
        dataset: source.dataset,
        location: source.location,
        dim: def.entity,
        value, from, to,
      });
      setDetails((d) => ({ ...d, [value]: s }));
    } catch (e) {
      const err = e as ApiError;
      setDetailError(err.hint ?? `Could not load history (${err.status}).`);
    } finally {
      setDetailBusy(null);
    }
  }

  return (
    <div className="shell">
      <header className="dash-head">
        <div>
          <h1>{property.name}</h1>
          <p className="muted num">
            {from} to {to}{source ? ` · ${source.dataset}` : ""}
          </p>
        </div>
        <button className="quiet" onClick={onChangeSources}>Change sources</button>
      </header>

      <div className="mnav">
        {metrics.map((m) => (
          <button key={m.id} className={m.id === active ? "on" : ""}
                  disabled={busy} onClick={() => setActive(m.id)}>
            {m.label}
          </button>
        ))}
      </div>

      <DateRangeSlider
        min={earliest} max={latest} from={from} to={to}
        onChange={(f, t) => setRange({ from: f, to: t })}
      />

      {demo && (
        <div className="warning">
          The analyses read Search Console directly and are not simulated. Sample
          data covers Explore, the funnel and the bridge.
        </div>
      )}
      {!source && (
        <div className="warning">
          This client has no Search Console export selected, so the analyses
          cannot run. Use Change sources to pick one.
        </div>
      )}
      {error && <div className="warning">{error}</div>}

      {def && (
        <section className="panel">
          <h2>{def.label}</h2>
          <p className="muted">{def.blurb}</p>

          <Explainer metricId={def.id} />

          {def.entity ? (
            <p className="hint">
              Click any row to open its weekly history for impressions, clicks,
              CTR and position.
            </p>
          ) : (
            <p className="hint muted">
              This view is already a time series, so rows do not expand. The
              per-query and per-page views do.
            </p>
          )}

          {busy && <p className="muted">Running against BigQuery.</p>}

          {!busy && result?.status === "timeout" && (
            <div className="warning">{result.hint}</div>
          )}

          {!busy && result?.status === "done" && (
            <>
              {def.render === "timeseries" && def.xKey && (
                <Line rows={rows} xKey={def.xKey}
                      yKey={(def.columns.find((c) => c.primary) ?? def.columns[1]).key}
                      format={(def.columns.find((c) => c.primary) ?? def.columns[1]).format} />
              )}

              {def.render === "stacked" && def.xKey && def.seriesKey && (
                <Stacked rows={rows} xKey={def.xKey} seriesKey={def.seriesKey}
                         valueKey={(def.columns.find((c) => c.primary) ?? def.columns[2]).key} />
              )}

              <Table
                rows={rows}
                columns={def.columns}
                sortKey={sortKey}
                sortDesc={desc}
                onSort={(k) => {
                  if (k === sortKey) setDesc(!desc);
                  else { setSortKey(k); setDesc(true); }
                }}
                entityKey={entityKey ?? undefined}
                expandedRow={openRow}
                onToggleRow={entityKey ? toggleRow : undefined}
                renderDetail={(v) => (
                  <EntityDetail
                    series={details[v] ?? null}
                    loading={detailBusy === v}
                    error={detailBusy === v ? null : detailError}
                  />
                )}
              />

              <p className="cost">
                Scanned {formatBytes(result.bytes ?? 0)} · about ${result.estUsd?.toFixed(4)} on
                your own BigQuery billing.
              </p>
            </>
          )}
        </section>
      )}

      {(debugMode() || failure !== null) && (
        <DebugPanel trace={lastTrace} error={failure ?? undefined} />
      )}
    </div>
  );
}

function sortRows(
  rows: Record<string, string | null>[], key: string | null, desc: boolean
) {
  if (!key) return rows;
  return [...rows].sort((a, b) => {
    const av = a[key], bv = b[key];
    const an = Number(av), bn = Number(bv);
    const numeric = Number.isFinite(an) && Number.isFinite(bn);
    const cmp = numeric ? an - bn : String(av ?? "").localeCompare(String(bv ?? ""));
    return desc ? -cmp : cmp;
  });
}

function formatBytes(b: number): string {
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
}

const isoOf = (t: number) => new Date(t).toISOString().slice(0, 10);
